import { type NoteFailurePresentedEventProps } from '@/models/analytics/features/notes';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { ConsultNoteStatus } from '@heidi/schema/ml-scribe';
import {
  FrontendNoteFailureReason,
  GET_SESSION_BY_ID,
  httpStatusFromErrorCode,
  isConsultNoteEmpty,
  StreamingStatus,
} from '@heidi/session/contracts';
import {
  captureGenerationAttemptBaseline,
  failArtifactGeneration,
  finishSessionGeneration,
  isLatestGeneration,
  noteFailurePresented,
  noteGenerationLogger,
  parseNoteFailedErrorCode,
  type PendingFailureCommit,
  writeSessionCache,
} from '@heidi/session/data';
import { isNoteGenerationRecoveryEnabled } from '@heidi/session/flags';
import { isTemplateDeleteProActionsEnabled } from '@heidi/templates/flags';

function toNoteFailurePresented(
  pending: PendingFailureCommit
): NoteFailurePresentedEventProps {
  const { restoreReadableNote, isHaikuIntegration } = pending;
  // Haiku pages use `useMinimalProviders` (no upgrade modals, Toaster only), and
  // `StructuredNoteContainer` renders a FAILED note without reading `feError`.
  const shownHeldReason = isHaikuIntegration ? undefined : pending.heldFeError;
  const shownSurface =
    isHaikuIntegration && pending.recoverySurface !== 'toast'
      ? undefined
      : pending.recoverySurface;
  const errorCode = parseNoteFailedErrorCode(pending.error);
  return {
    session_id: pending.sessionId,
    error_code: errorCode,
    http_status: pending.httpStatus ?? httpStatusFromErrorCode(errorCode),
    had_readable_note: !isConsultNoteEmpty(pending.previousResult),
    recovery_surface: shownSurface ?? 'none',
    note_outcome: restoreReadableNote
      ? 'restored_note'
      : shownHeldReason
        ? 'held_state'
        : 'failed_overlay',
    held_reason: shownHeldReason ?? null,
    template_delete_recovery_enabled: isTemplateDeleteProActionsEnabled(),
    generation_recovery_enabled: isNoteGenerationRecoveryEnabled(),
    is_haiku_integration: isHaikuIntegration ?? false,
  };
}

export function reportNoteFailurePresented(pending: PendingFailureCommit): void {
  noteFailurePresented(toNoteFailurePresented(pending));
}

/**
 * Commit the generic terminal failure for one generation attempt: restore the
 * previous result, mark the consult note FAILED, drop to NOT_STARTED and
 * refetch the session so the backend's `failure_reason` reaches the overlay.
 * Client-held failures (`heldFeError`) keep their reason without refetching,
 * since a GET would drop the client-only `feError`. `restoreReadableNote`
 * skips the FAILED stamp. Reports `note_failure_presented` when a cached
 * session was written.
 *
 * `finish: false` is the inline path: the body calls this inside `onError`
 * while the generation is current, and finishes the handle itself later.
 *
 * Both paths reject stale handles before any write.
 * `finish: true` is the deferred path: a recovery layer held the commit back
 * while it decided whether to retry, so by now a newer generation may own the
 * session cache and the artifact machine. In that case the stale attempt has
 * nothing left to commit; `beginSessionGeneration` already superseded its
 * handle, and writing FAILED here would clobber the newer attempt.
 */
export function commitFailedGeneration(
  pending: PendingFailureCommit,
  { finish }: { finish: boolean }
): void {
  const {
    sessionId,
    previousResult,
    baseline,
    handle,
    error,
    heldFeError,
    restoreReadableNote,
  } = pending;
  if (!isLatestGeneration(handle)) {
    pending.settleDeferredTelemetry?.(undefined, false);
    return;
  }
  if (finish) failArtifactGeneration({ kind: 'note', sessionId }, error);
  const write = writeSessionCache(sessionId, (oldData) => ({
    ...oldData,
    abortController: new AbortController(),
    streamingStatus: StreamingStatus.NOT_STARTED,
    ...(heldFeError && { feError: heldFeError }),
    consult_note: {
      ...oldData.consult_note,
      ...(baseline ? captureGenerationAttemptBaseline(baseline) : {}),
      result: previousResult,
      ...(!restoreReadableNote && { status: ConsultNoteStatus.FAILED }),
      // The pane reads `failure_reason ?? feError`, so a server reason
      // stamped by an earlier attempt would mask the held state.
      ...(heldFeError && { failure_reason: null }),
    },
  }));
  // With no cached session nothing is on screen, so nothing is presented.
  if (write && !pending.settleDeferredTelemetry) reportNoteFailurePresented(pending);
  // A refetch would replace the held FAILED state with the server's.
  let refetchPromise: Promise<void> | undefined;
  if (!heldFeError) {
    refetchPromise = reactQueryClient.invalidateQueries({
      queryKey: [GET_SESSION_BY_ID, { sessionId }],
    });
  } else if (heldFeError === FrontendNoteFailureReason.OFFLINE) {
    noteGenerationLogger.info('onerror classified offline', { sessionId });
  }
  if (finish) finishSessionGeneration(handle);
  pending.settleDeferredTelemetry?.(refetchPromise, Boolean(write));
}
